// Shared by the Layout primitives (Stack, Container, Grid). Class names here are assembled from
// template strings, so Tailwind's JIT never sees them in source — the safelist block in
// tailwind.config.js is what makes them exist. Adding a value to Spacing, or a new prefix to
// SPACING_PREFIX, means updating that safelist too or the class silently resolves to nothing.

export type Breakpoint = ScreenBreakpoint | ContainerBreakpoint;

export type ScreenBreakpoint = "default" | "sm" | "md" | "lg" | "xl" | "2xl";

// Width of the nearest ancestor carrying `queryContainer`, not the viewport. The plugin registers
// these after the core screen variants, so a container key beats a screen key when both match.
export type ContainerBreakpoint = "@sm" | "@md" | "@lg" | "@xl" | "@2xl";

export type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

export type Spacing = 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type MarginSpacing = Spacing | "auto";

export interface SpacingProps {
	margin?: ResponsiveValue<MarginSpacing>;
	marginTop?: ResponsiveValue<MarginSpacing>;
	marginBottom?: ResponsiveValue<MarginSpacing>;
	marginLeft?: ResponsiveValue<MarginSpacing>;
	marginRight?: ResponsiveValue<MarginSpacing>;
	marginX?: ResponsiveValue<MarginSpacing>;
	marginY?: ResponsiveValue<MarginSpacing>;
	padding?: ResponsiveValue<Spacing>;
	paddingTop?: ResponsiveValue<Spacing>;
	paddingBottom?: ResponsiveValue<Spacing>;
	paddingLeft?: ResponsiveValue<Spacing>;
	paddingRight?: ResponsiveValue<Spacing>;
	paddingX?: ResponsiveValue<Spacing>;
	paddingY?: ResponsiveValue<Spacing>;
}

/**
 * Turns a responsive prop into Tailwind classes. A scalar yields one class; an object yields one
 * per breakpoint present, `default` unprefixed — it is the 0px base, not a screen. `@`-prefixed
 * keys emit container-query variants, which need a `queryContainer` ancestor to ever match.
 */
export function responsive<T extends string | number>(
		value: ResponsiveValue<T> | undefined,
		toClass: (value: T) => string
): string {
	return variantClasses(value, BREAKPOINT_PREFIX, toClass);
}

/**
 * The engine behind `responsive` and `stateful` (lib/color.ts): a scalar yields one class, an
 * object yields one per key present, in the order `prefixes` declares — which is the order the
 * cascade wants them in.
 */
export function variantClasses<K extends string, T extends string | number>(
		value: T | Partial<Record<K, T>> | undefined,
		prefixes: Record<K, string>,
		toClass: (value: T) => string
): string {
	if (value === undefined || value === null) return "";
	if (typeof value !== "object") return toClass(value);

	return (Object.entries(prefixes) as Array<[K, string]>)
		.filter(([key]) => value[key] !== undefined)
		.map(([key, prefix]) => `${prefix}${toClass(value[key] as T)}`)
		.join(" ");
}

/**
 * Splits the 14 margin/padding props off a prop bag in one pass: their classes on one side, the
 * DOM-safe remainder on the other. Without the split, `paddingX` and friends land on the element
 * as unknown React attributes.
 */
export function splitSpacing<P extends SpacingProps>(props: P): {className: string, rest: Omit<P, keyof SpacingProps>} {
	const classes: string[] = [];
	const rest = {...props} as Record<string, unknown>;

	for (const [prop, prefix] of Object.entries(SPACING_PREFIX)) {
		const value = rest[prop] as ResponsiveValue<MarginSpacing> | undefined;
		delete rest[prop];
		if (value !== undefined) classes.push(responsive(value, spacing => `${prefix}-${spacing}`));
	}

	return {className: classes.join(" "), rest: rest as Omit<P, keyof SpacingProps>};
}

// Ordered narrow-to-wide so the emitted class string reads the way the cascade applies. `default`
// is the 0px base — a plain class, no variant.
const BREAKPOINT_PREFIX: Record<Breakpoint, string> = {
	default: "",
	sm: "sm:",
	md: "md:",
	lg: "lg:",
	xl: "xl:",
	"2xl": "2xl:",
	"@sm": "@sm:",
	"@md": "@md:",
	"@lg": "@lg:",
	"@xl": "@xl:",
	"@2xl": "@2xl:"
};

const SPACING_PREFIX: Record<keyof SpacingProps, string> = {
	margin: "m",
	marginTop: "mt",
	marginBottom: "mb",
	marginLeft: "ml",
	marginRight: "mr",
	marginX: "mx",
	marginY: "my",
	padding: "p",
	paddingTop: "pt",
	paddingBottom: "pb",
	paddingLeft: "pl",
	paddingRight: "pr",
	paddingX: "px",
	paddingY: "py"
};
